<template>
  <nav :aria-label="$t('navigation.aria.main')">
    <ul
      v-if="mainNav"
      class="lg:flex main-nav justify-center hidden w-full h-full"
      :class="{ 'is-open': activeNavItemIndex !== null }"
      @mouseleave="setActiveNavItemIndex(null)"
      role="menubar"
    >
      <li
        v-for="(navTop, idx) in mainNav"
        :key="idx"
        class="main-nav-item"
        data-testid="main-nav-top"
        :class="{ 'is-active': activeNavItemIndex === idx }"
        role="none"
        @mouseenter="
          navTop.navBlocks && navTop.navBlocks.length > 0 ? setActiveNavItemIndex(idx) : setActiveNavItemIndex(null)
        "
      >
        <LinkField
          v-bind="navTop.link[0]"
          class="lg:px-8 fluid-xs leading-snug uppercase font-medium main-nav-item-link text-blackly-dark relative flex items-center h-full px-4 ring-inset"
          :active-class="'color-br-green is-active'"
          :aria-expanded="activeNavItemIndex === idx ? 'true' : 'false'"
          aria-haspopup="true"
          :aria-controls="`nav-drawer-${idx}`"
          role="menuitem"
          no-tracking
          @click.native="handleNavClick(navTop, idx)"
          @keydown.enter.prevent="toggleSubmenu(navTop, idx)"
          @keydown.space.prevent="toggleSubmenu(navTop, idx)"
          @keydown.right.prevent="moveToNextItem(idx)"
          @keydown.left.prevent="moveToPreviousItem(idx)"
          @keydown.esc.prevent="setActiveNavItemIndex(null)"
          @focus="handleMenuItemFocus(idx)"
        >
          {{ navTop.title }}
          <div
            class="indicator bg-br-green absolute bottom-0 left-0 right-0 transition-all opacity-0"
            aria-hidden="true"
          />
        </LinkField>

        <div
          v-if="renderDrawerContent && navTop.navBlocks && navTop.navBlocks.length > 0"
          class="drawer absolute px-16 py-10"
          role="menu"
          :id="`nav-drawer-${idx}`"
          :data-nav-segment="snakeCase(navTop.title)"
          aria-label="Submenu"
          @keydown.esc.prevent="closeSubmenuAndFocusParent(idx)"
        >
          <div class="drawer-content flex justify-center w-full" data-testid="main-nav-drawer">
            <NavBlocks :blocks="navTop.navBlocks" />
          </div>
        </div>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { snakeCase } from 'lodash-es';
import NavBlocks from '@/components/layout/header/navBlocks/NavBlocks';
import LinkField from '@/components/common/LinkField';
import { useMainNavStore } from '@/stores/main-nav';

/**
 * renders navTop items SSR, renders navBlocks (drawerContent) ClientSide only
 */
const mainNavStore = useMainNavStore();
const renderDrawerContent = ref(false);

// Computed properties
const mainNav = computed(() => mainNavStore.mainNav);
const activeNavItemIndex = computed(() => mainNavStore.activeNavItemIndex);

// Methods
const setActiveNavItemIndex = (index) => {
  mainNavStore.setActiveNavItemIndex(index);
};

// Enhanced keyboard navigation methods
const openSubmenu = (index) => {
  setActiveNavItemIndex(index);
  nextTick(() => {
    const submenu = document.getElementById(`nav-drawer-${index}`);
    if (submenu) {
      const firstFocusable = submenu.querySelector(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  });
};

const closeSubmenuAndFocusParent = (index) => {
  setActiveNavItemIndex(null);
  nextTick(() => {
    // Focus the parent menu item
    const menuItems = document.querySelectorAll('.main-nav-item-link');
    if (menuItems && menuItems[index]) {
      menuItems[index].focus();
    }
  });
};

const moveToNextItem = (currentIndex) => {
  const nextIndex = (currentIndex + 1) % mainNav.value.length;
  nextTick(() => {
    const menuItems = document.querySelectorAll('.main-nav-item-link');
    if (menuItems && menuItems[nextIndex]) {
      menuItems[nextIndex].focus();
    }
  });
};

const moveToPreviousItem = (currentIndex) => {
  const prevIndex = (currentIndex - 1 + mainNav.value.length) % mainNav.value.length;
  nextTick(() => {
    const menuItems = document.querySelectorAll('.main-nav-item-link');
    if (menuItems && menuItems[prevIndex]) {
      menuItems[prevIndex].focus();
    }
  });
};

const toggleSubmenu = (navTop, index) => {
  if (navTop.navBlocks && navTop.navBlocks.length > 0) {
    if (activeNavItemIndex.value === index) {
      closeSubmenuAndFocusParent(index);
    } else {
      openSubmenu(index);
    }
  } else {
    // If no submenu, treat as a regular click
    handleNavClick(navTop, index);
  }
};

const handleMenuItemFocus = (index) => {
  if (activeNavItemIndex.value !== null && activeNavItemIndex.value !== index) {
    setActiveNavItemIndex(null);
  }
};

const handleNavClick = (navTop, idx) => {
  const path = snakeCase(navTop.title);
  useNuxtApp().$_pushNavigationEvent('header', path);

  // Toggle submenu if it exists
  if (navTop.navBlocks && navTop.navBlocks.length > 0) {
    toggleSubmenu(navTop, idx);
  } else {
    setActiveNavItemIndex(null); // Close any open submenu
  }
};

// Lifecycle hooks
onMounted(() => {
  renderDrawerContent.value = true;

  // Add global event listener for Escape key to close submenus
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeNavItemIndex.value !== null) {
      setActiveNavItemIndex(null);
    }
  });
});

onUnmounted(() => {
  // Clean up event listener
  document.removeEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeNavItemIndex.value !== null) {
      setActiveNavItemIndex(null);
    }
  });
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
.indicator {
  @apply h-[3px] mb-[-1px] transition-all delay-[250];
}
.main-nav:not(.is-open) .main-nav-item-link.is-active .indicator {
  @apply opacity-100;
}
.main-nav-item.is-active .indicator,
.main-nav-item:hover .indicator {
  @apply opacity-100;
}

.drawer {
  @apply mt-[1px] left-0 right-0 top-full min-h-[300px] overflow-hidden opacity-0 invisible scale-y-0;
  background-color: #fcfcfc;
  transition:
    transform 100ms ease,
    opacity 0ms linear 100ms,
    visibility 0ms linear 100ms;
  transform-origin: top center;
}

.drawer-content {
  @apply opacity-0 translate-y-[-10px] delay-[0];
  transition:
    transform 0.1s,
    opacity 100ms;
}

.main-nav.is-open .drawer {
  @apply transition-none scale-y-100;
}

.main-nav-item.is-active .drawer {
  @apply flex opacity-100 visible scale-y-100;
  transition:
    transform 250ms ease,
    opacity 0ms,
    visibility 0ms;
}

.main-nav-item.is-active .drawer-content {
  @apply opacity-100 translate-y-0 delay-200;
  transition:
    transform 300ms ease,
    opacity 200ms linear;
}
</style>
